'use client';

import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import type { CompanyEnrichmentData } from '@/lib/backend-api';
import { trackSignupAndRedirect } from '@/lib/track-signup-click';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LOCALE_MAP } from './companyDetailUtils';

/**
 * Guest-facing blurred placeholder shown when `basicData.has_funding_data` is set.
 *
 * Renders the same two-column "Last Round / Total" layout as {@link FundingCard},
 * but with fake numbers and a blur overlay to encourage sign-in.
 */
export function FundingCardPlaceholder({
  euId,
  className,
}: {
  euId?: string;
  /** Layout classes from the page (mobile `order-*`, borders) — see MOBILE_ORDER. */
  className?: string;
}) {
  const { t } = useTranslation();
  const pathname = usePathname();
  return (
    <Card variant="grid" className={className}>
      <CardHeader>
        <div>
          <CardTitle as="h2">{t('companyDetail.funding', 'Funding')}</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                trackSignupAndRedirect(pathname, 'funding', euId);
              }}
              className="underline hover:text-foreground transition-colors"
            >
              {t('companyHistory.signIn', 'Sign in')}
            </a>{' '}
            {t('companyDetail.toViewFunding', 'to view funding data')}
          </p>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 divide-x divide-border select-none blur-[8px]">
          <div className="flex flex-col items-center text-center pr-6">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
              {t('companyDetail.fundingLastRound', 'Last Round')}
            </p>
            <p className="text-4xl font-bold tracking-tight text-foreground leading-none">
              €12.8M
            </p>
            <p className="text-sm text-muted-foreground mt-1.5">
              Series B · Mar 2025
            </p>
          </div>
          <div className="flex flex-col items-center text-center pl-6">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
              {t('companyDetail.fundingTotal', 'Total')}
            </p>
            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <p className="text-2xl font-bold text-foreground leading-none">
                  4
                </p>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mt-0.5">
                  {t('companyDetail.fundingRoundsLabel', 'Rounds')}
                </p>
              </div>
              <div className="flex flex-col items-center">
                <p className="text-2xl font-bold text-foreground leading-none">
                  7
                </p>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mt-0.5">
                  {t('companyDetail.fundingInvestorsLabel', 'Investors')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Signed-in funding summary derived from LinkedIn enrichment data.
 *
 * Shows either one or both columns depending on which data is present; returns
 * null when no funding data is available. Currency/amount formatting follows the
 * active language via {@link LOCALE_MAP}.
 */
export function FundingCard({
  linkedin,
  language,
  className,
}: {
  linkedin: CompanyEnrichmentData;
  language: string;
  /** Layout classes from the page (mobile `order-*`, borders) — see MOBILE_ORDER. */
  className?: string;
}) {
  const { t, i18n } = useTranslation();

  const formattedLastRoundAmount = useMemo(() => {
    if (linkedin.funding_last_round_amount == null) return null;
    const locale = LOCALE_MAP[language] || 'en-US';
    const currency = linkedin.funding_last_round_currency || 'EUR';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(linkedin.funding_last_round_amount);
    } catch {
      return `${linkedin.funding_last_round_amount.toLocaleString(locale)} ${currency}`;
    }
  }, [
    linkedin.funding_last_round_amount,
    linkedin.funding_last_round_currency,
    language,
  ]);

  const formattedLastRoundDate = useMemo(() => {
    if (!linkedin.funding_last_round_date) return null;
    try {
      return new Date(linkedin.funding_last_round_date).toLocaleDateString(
        i18n.language === 'de'
          ? 'de-DE'
          : i18n.language === 'ar'
            ? 'ar-SA'
            : 'en-US',
        { year: 'numeric', month: 'short' },
      );
    } catch {
      return linkedin.funding_last_round_date;
    }
  }, [linkedin.funding_last_round_date, i18n.language]);

  const hasLastRoundData = formattedLastRoundAmount != null;
  const hasTotalData =
    linkedin.funding_rounds != null || linkedin.funding_investor_count != null;
  const hasFundingData =
    hasLastRoundData ||
    hasTotalData ||
    linkedin.funding_last_round_type != null;

  if (!hasFundingData) return null;

  const twoColumns = hasLastRoundData && hasTotalData;

  return (
    <Card variant="grid" className={className}>
      <CardHeader>
        <CardTitle as="h2">{t('companyDetail.funding', 'Funding')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={
            twoColumns
              ? 'grid grid-cols-2 divide-x divide-border'
              : 'flex justify-center'
          }
        >
          {hasLastRoundData && (
            <div
              className={cn(
                'flex flex-col items-center text-center',
                twoColumns ? 'pr-6' : 'w-full',
              )}
            >
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                {t('companyDetail.fundingLastRound', 'Last Round')}
              </p>
              <p className="text-4xl font-bold tracking-tight text-foreground leading-none">
                {formattedLastRoundAmount}
              </p>
              {(linkedin.funding_last_round_type || formattedLastRoundDate) && (
                <p className="text-sm text-muted-foreground mt-1.5">
                  {[linkedin.funding_last_round_type, formattedLastRoundDate]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              )}
            </div>
          )}

          {hasTotalData && (
            <div
              className={cn(
                'flex flex-col items-center text-center',
                twoColumns ? 'pl-6' : 'w-full',
              )}
            >
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                {t('companyDetail.fundingTotal', 'Total')}
              </p>
              <div className="flex gap-4">
                {linkedin.funding_rounds != null && (
                  <div className="flex flex-col items-center">
                    <p className="text-2xl font-bold text-foreground leading-none">
                      {linkedin.funding_rounds}
                    </p>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mt-0.5">
                      {t('companyDetail.fundingRoundsLabel', 'Rounds')}
                    </p>
                  </div>
                )}
                {linkedin.funding_investor_count != null && (
                  <div className="flex flex-col items-center">
                    <p className="text-2xl font-bold text-foreground leading-none">
                      {linkedin.funding_investor_count}
                    </p>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mt-0.5">
                      {t('companyDetail.fundingInvestorsLabel', 'Investors')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
